/**
 * SOLOMON — INTEGRATIONS PANEL
 * Glass card list for OAuth integrations inside the settings accordion.
 *
 * Google is a unified connection — one OAuth flow gives all Google services.
 * QuickBooks and Broker API remain "coming soon" placeholders.
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './integrations.css';

// ── Integration definitions ─────────────────────────────────────

const INTEGRATIONS = [
  {
    id: 'google',
    name: 'Google Workspace',
    description: 'Gmail, Calendar, Sheets, Tasks — one connection',
    icon: GoogleIcon,
    available: true,
    // Drive and Contacts are deliberately NOT listed. Their routes and (for
    // Contacts) the service itself were deleted in `555c03a` (Issue 62,
    // 2026-08-04), and no chat tool has ever reached either — `assembleNativeTools`
    // exposes no Google tool at all. Listing them here advertised capabilities
    // three other subsystems correctly report as absent (capability registry,
    // tool assembler, identity prompt), which is what made Solomon look wrong
    // when it truthfully said it had no Drive access.
    // These four are real: they feed the daily briefing via the Google read-arms
    // in `briefingService.js`. Do not re-add a service chip without a route or
    // tool actually reaching it.
    services: [
      { id: 'gmail', name: 'Gmail' },
      { id: 'calendar', name: 'Calendar' },
      { id: 'sheets', name: 'Sheets' },
      { id: 'tasks', name: 'Tasks' },
    ],
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    description: 'Financial reports, invoices, cash flow',
    icon: QuickBooksIcon,
    available: false,
  },
  {
    id: 'broker',
    name: 'Broker API',
    description: 'Trading data and portfolio monitoring',
    icon: BrokerIcon,
    available: false,
  },
];

// ── Main component ──────────────────────────────────────────────

export default function IntegrationsPanel({
  gmailConnected = false,
  onConnectGmail,
  onDisconnectGmail,
}) {
  const { authFetch } = useAuth();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  // Data backbone state
  const [backboneRegistry, setBackboneRegistry] = useState(null);
  const [backboneLoading, setBackboneLoading] = useState(false);
  const [backboneInitializing, setBackboneInitializing] = useState(false);

  // Google connection state — same OAuth tokens power all Google services
  const googleConnected = gmailConnected;

  // API base must be defined before any useEffect that depends on it
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  const handleConnect = useCallback(async (integrationId) => {
    if (integrationId !== 'google') return;
    setConnecting(true);
    try {
      await onConnectGmail?.();
    } finally {
      setConnecting(false);
    }
  }, [onConnectGmail]);

  const handleDisconnect = useCallback(async (integrationId) => {
    if (integrationId !== 'google') return;
    setDisconnecting(integrationId);
    try {
      await onDisconnectGmail?.();
    } finally {
      setDisconnecting(null);
      setConfirmDisconnect(null);
    }
  }, [onDisconnectGmail]);

  // Fetch backbone registry when Google is connected
  const fetchBackboneRegistry = useCallback(async () => {
    if (!googleConnected) return;
    setBackboneLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/sheets/registry`);
      if (res.ok) {
        const data = await res.json();
        setBackboneRegistry(data.data);
      }
    } catch (err) {
      console.warn('[Solomon/Backbone] Registry fetch failed:', err.message);
    } finally {
      setBackboneLoading(false);
    }
  }, [googleConnected, authFetch, API_BASE]);

  useEffect(() => { fetchBackboneRegistry(); }, [fetchBackboneRegistry]);

  const handleInitializeBackbone = useCallback(async () => {
    setBackboneInitializing(true);
    try {
      const res = await authFetch(`${API_BASE}/api/sheets/initialize`, { method: 'POST' });
      if (res.ok) {
        await fetchBackboneRegistry();
      }
    } catch (err) {
      console.error('[Solomon/Backbone] Initialize failed:', err.message);
    } finally {
      setBackboneInitializing(false);
    }
  }, [authFetch, API_BASE, fetchBackboneRegistry]);

  const BACKBONE_SHEETS = [
    { key: 'content_calendar', label: 'Content Calendar' },
    { key: 'financial_ledger', label: 'Financial Ledger' },
    { key: 'contact_tracker', label: 'Contact Tracker' },
  ];

  return (
    <div className="integrations-list">
      {INTEGRATIONS.map(integration => {
        const isGoogle = integration.id === 'google';
        const connected = isGoogle && googleConnected;
        const isAvailable = integration.available;
        const IconComponent = integration.icon;

        return (
          <div
            key={integration.id}
            className={`integration-card ${!isAvailable ? 'integration-card--disabled' : ''} ${connected ? 'integration-card--connected' : ''}`}
          >
            <div className="integration-card-left">
              <div className={`integration-card-icon ${connected ? 'integration-card-icon--connected' : ''}`}>
                <IconComponent />
              </div>
              <div className="integration-card-info">
                <div className="integration-card-name">
                  {integration.name}
                  {!isAvailable && (
                    <span className="integration-card-soon">Coming soon</span>
                  )}
                </div>
                <div className="integration-card-desc">{integration.description}</div>
                {isGoogle && connected && integration.services && (
                  <div className="integration-services">
                    {integration.services.map(svc => (
                      <span key={svc.id} className="integration-service-badge">
                        <span className="integration-service-check">✓</span>
                        {svc.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="integration-card-right">
              {connected ? (
                <>
                  {confirmDisconnect === integration.id ? (
                    <div className="integration-confirm">
                      <span className="integration-confirm-text">Remove?</span>
                      <button
                        className="integration-confirm-btn integration-confirm-btn--yes"
                        onClick={() => handleDisconnect(integration.id)}
                        disabled={disconnecting === integration.id}
                      >
                        {disconnecting === integration.id ? '...' : 'Yes'}
                      </button>
                      <button
                        className="integration-confirm-btn integration-confirm-btn--no"
                        onClick={() => setConfirmDisconnect(null)}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className="integration-btn integration-btn--disconnect"
                      onClick={() => setConfirmDisconnect(integration.id)}
                    >
                      Disconnect
                    </button>
                  )}
                </>
              ) : isAvailable ? (
                <button
                  className="integration-btn integration-btn--connect"
                  onClick={() => handleConnect(integration.id)}
                  disabled={connecting}
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
              ) : (
                <div className="integration-card-status">
                  <span className="integration-status-dot integration-status-dot--disabled" />
                  <span className="integration-status-text integration-status-text--disabled">Not available</span>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Data Backbone Section — only visible when Google is connected */}
      {googleConnected && (
        <div className="integration-backbone">
          <div className="integration-backbone-header">
            <div className="integration-card-name">Data Sheets</div>
            <div className="integration-card-desc">Solomon's spreadsheet templates in your Google Drive</div>
          </div>

          {backboneLoading ? (
            <div className="integration-card-desc" style={{ padding: '8px 0' }}>Loading...</div>
          ) : backboneRegistry?.initialized ? (
            <div className="integration-backbone-list">
              {BACKBONE_SHEETS.map(sheet => {
                const entry = backboneRegistry.sheets.find(s => s.sheet_name === sheet.key);
                return (
                  <div key={sheet.key} className="integration-backbone-item">
                    <span className="integration-service-check" style={{ color: entry ? '#1D9E75' : 'rgba(255,255,255,0.2)' }}>
                      {entry ? '✓' : '○'}
                    </span>
                    <span className={`integration-backbone-label ${entry ? '' : 'integration-backbone-label--pending'}`}>
                      {sheet.label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <button
              className="integration-btn integration-btn--connect"
              onClick={handleInitializeBackbone}
              disabled={backboneInitializing}
              style={{ marginTop: 8 }}
            >
              {backboneInitializing ? 'Creating sheets...' : 'Set Up Data Sheets'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────

function GoogleIcon() {
  // 2x2 apps-grid icon representing workspace/suite
  // (not the Google logo — brand guideline neutral)
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function QuickBooksIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function BrokerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="10" width="18" height="11" rx="2" />
      <path d="M12 2L2 10h20L12 2z" />
      <line x1="7" y1="14" x2="7" y2="17" />
      <line x1="12" y1="14" x2="12" y2="17" />
      <line x1="17" y1="14" x2="17" y2="17" />
    </svg>
  );
}
